import { Download, MicIcon, Play, Pause, Trash } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import WaveSurfer from "wavesurfer.js";
import RecordPlugin from "wavesurfer.js/dist/plugins/record";
import { Form } from "react-bootstrap";
import { BtnAyuda } from "./BtnAyuda";

const AudioRecorder = ({ item, esEditable, index, idx, reg, error }) => {
    const micContainerRef = useRef(null);
    const [recording, setRecording] = useState(false);
    // Inicializa recordedUrl con item.valor si existe
    const [recordedUrl, setRecordedUrl] = useState(item.valor || null);
    const [recordedData, setRecordedData] = useState(null);
    const [wavesurfer, setWaveSurfer] = useState(null);
    const [recordPlugin, setRecordPlugin] = useState(null);
    const [recordingTime, setRecordingTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [errorMsg, setErrorMsg] = useState(null);
    const recordingIntervalRef = useRef(null);

    const blobToBase64 = (blob) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    useEffect(() => {
        // Verifica compatibilidad del navegador
        if (
            !navigator.mediaDevices ||
            !navigator.mediaDevices.getUserMedia ||
            typeof window.MediaRecorder === "undefined"
        ) {
            setErrorMsg("Tu navegador no soporta la grabación de audio.");
            return;
        }
        setErrorMsg(null);

        let wavesurferInstance;
        let recordPluginInstance;
        try {
            wavesurferInstance = WaveSurfer.create({
                container: micContainerRef.current,
                waveColor: "#a0aec0",
                progressColor: "#3b82f6",
                height: 56,
                barGap: 2,
                barWidth: 3,
                scrollParent: true,
                minPxPerSec: 0.5,
                sampleRate: 8000,
            });
            setWaveSurfer(wavesurferInstance);

            recordPluginInstance = RecordPlugin.create();
            setRecordPlugin(recordPluginInstance);

            wavesurferInstance.registerPlugin(recordPluginInstance);

            recordPluginInstance.on("record-end", async (blob) => {
                const url = URL.createObjectURL(blob);
                setRecordedUrl(url);
                let base64Data = await blobToBase64(blob);
                setRecordedData(base64Data);
                // Carga el audio grabado en la misma instancia para reproducirlo
                wavesurferInstance.load(url);
            });

            // Si hay un valor inicial, cargarlo en wavesurfer
            if (item.valor) {
                wavesurferInstance.load(item.valor);
            }

            wavesurferInstance.on("play", () => setIsPlaying(true));
            wavesurferInstance.on("pause", () => setIsPlaying(false));
            wavesurferInstance.on("finish", () => setIsPlaying(false));
            wavesurferInstance.on("audioprocess", () => {
                setCurrentTime(wavesurferInstance.getCurrentTime());
            });
            wavesurferInstance.on("ready", () => {
                setDuration(wavesurferInstance.getDuration());
            });
        } catch (err) {
            setErrorMsg("Ocurrió un error al inicializar el grabador de audio.");
        }

        return () => {
            if (wavesurferInstance) wavesurferInstance.destroy();
        };
        // Dependencias: item.valor para recargar si cambia el valor inicial
    }, [item.valor]);

    useEffect(() => {
        if (recording) {
            setRecordingTime(0);
            recordingIntervalRef.current = setInterval(() => {
                setRecordingTime((prev) => prev + 1);
            }, 1000);
        } else {
            clearInterval(recordingIntervalRef.current);
        }
        return () => clearInterval(recordingIntervalRef.current);
    }, [recording]);

    const handleRecordClick = async (e) => {
        e.preventDefault();
        setErrorMsg(null);
        if (recordPlugin && wavesurfer) {
            if (recordPlugin.isRecording()) {
                recordPlugin.stopRecording();
                setRecording(false);
            } else {
                // Limpia cualquier audio previo antes de grabar
                if (recordedUrl) {
                    wavesurfer.empty();
                    setRecordedUrl(null);
                }
                try {
                    await recordPlugin.startRecording();
                    setRecording(true);
                } catch (err) {
                    if (err && err.name === "NotAllowedError") {
                        setErrorMsg("Permiso de micrófono denegado. Por favor, permite el acceso al micrófono.");
                    } else if (err && err.name === "NotFoundError") {
                        setErrorMsg("No se encontró ningún micrófono disponible.");
                    } else {
                        setErrorMsg("No se pudo iniciar la grabación. Verifica los permisos del micrófono.");
                    }
                    setRecording(false);
                }
            }
        }
    };

    const handlePlayClick = (e) => {
        e.preventDefault();
        if (wavesurfer && recordedUrl) {
            wavesurfer.playPause();
        }
    };

    const handleDeleteClick = (e) => {
        e.preventDefault();
        if (wavesurfer) {
            wavesurfer.empty();
            setRecordedUrl(null);
            setRecordedData(null);
            setRecording(false);
            setRecordingTime(0);
            setIsPlaying(false);
            setCurrentTime(0);
            setDuration(0);
        }
    };


    // Helper para formatear el tiempo mm:ss
    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, "0");
        const s = Math.floor(seconds % 60).toString().padStart(2, "0");
        return `${m}:${s}`;
    };

    return (
        <Form.Group style={{ display: 'block' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-start' }} >
                <Form.Label style={{ color: item.label_color ? item.label_color : "#000", fontSize: item.font_size ? item.font_size : "" }} >{item.titulo}{item.es_requerido === true && '*'}</Form.Label>
                {item?.ayuda &&
                    <BtnAyuda ayuda={item.ayuda} />
                }
            </div>
            <div className="recorder">

                <div className="recorder__main">
                    <div
                        ref={micContainerRef}
                        className={`recorder__microphone-waveform${recording ? " recorder__microphone-waveform--recording" : ""}${recordedUrl ? " recorder__recorded-audio-waveform--visible" : ""}`}
                    >
                        {/* El visualizador se usa tanto para grabar como para reproducir */}
                    </div>
                    <button
                        id="record"
                        className={`recorder__button  ${recording ? "recorder__button--recording" : ""}`}
                        onClick={handleRecordClick}
                        disabled={recording && !recordPlugin?.isRecording() || esEditable}
                    >

                        {recording ? "" : <MicIcon />}
                        {recording && (
                            <span>
                                {formatTime(recordingTime)}
                            </span>
                        )}
                    </button>

                </div>
                {recordedUrl && (
                    <div className="recorder__controls">
                        <button onClick={handlePlayClick} className="player__button">
                            {isPlaying ? <Pause /> : <Play />}
                        </button>
                        <span className="player__time">
                            {formatTime(currentTime)} / {formatTime(duration)}
                        </span>
                        <button
                            onClick={handleDeleteClick}
                            title="Eliminar grabación"
                            className="player__button player__button--delete"
                            disabled={esEditable}>
                            <Trash />
                        </button>
                        <a
                            href={recordedUrl}
                            title="Descargar grabación"
                            className="player__button player__button--download"
                            download={
                                "recording." +
                                ((recordPlugin?.recordedData?.type || "audio/webm").split(";")[0].split("/")[1] || "webm")
                            }
                        >
                            <Download />
                        </a>

                    </div>
                )}
            </div>
            <input
                type="hidden"
                name={`asesoria.${index}.${item.nombre}`}
                value={recordedData || ""}
                key={`${index} - ${idx}`}
                ref={reg}
                {...reg(`asesoria.${index}.${item.nombre}`, {
                    required: item.es_requerido
                })}
            />
            {error ?
                (
                    error.type === 'required' ?
                        (
                            <span className="inputsInvalid">
                                Este campo es requerido
                            </span>
                        )
                        : null)
                : null}
            {errorMsg && <span className="inputsInvalid d-block">{errorMsg}</span>}

        </Form.Group>
    );
};

export default AudioRecorder;
